import { sendRequestTaskEmail } from '../../../../api'
import { REQUEST_API_ALIASES } from '../../../../constants/api'
import { REQUEST_ENTITY } from '../../../../constants/entity'
import { useAsyncCallback, useMinimizedModalsContext } from '../../../../hooks'
import { postApi } from '../../../../utils/api'
import { AITextGenerationLocalContextType } from '../../../AITextGeneration'
import {
  isDraftOpenAction,
  resolveMinimizedDraftEntryOpen,
} from '../../../Draft'
import {
  clearEmailDraftIfCreateNew,
  EMAIL_CREATION_FORM_TEMPLATE_FOLDER_REQUEST,
  EmailCreationPopUp,
  EmailDraftCreateActionSelectModal,
  getEmailFormTemplateEntityApiParams,
  isDirtyTableEmailCreationForm,
  useEmailCreationFormInitialValues,
  useEmailDraft,
  useEmailGetActiveAccountForCreate,
  useMassMailingAsyncAction,
} from '../../../Email'
import { MINIMIZED_MODAL_KEY } from '../../../Modal'
import { toUserSelectItems } from '../../../User'
import { updateTaskEmail } from '../../Task.api'
import { TASK_ACTION_SEND_AUDIENCE } from '../../Task.constants'

import {
  getEmailCreationPopUpFieldsProps,
  getEmailCreationPopUpSourceId,
  getRequestDataByTaskParams,
  getThreadParams,
} from './useTaskSendEmailTableAction.utils'

export function useTaskSendEmailTableAction({
  attachProps,
  outboundUuid,
  recipientsFilterModalInitialValues,
  templateListApiParams,
  withServiceAccount = false,
  onCloseEmailFormModal,
  onCloseModal,
  onReload,
  onSetEmailFormModal,
  onSetModal,
}) {
  const { activeAccountForCreate } = useEmailGetActiveAccountForCreate({
    withServiceAccount,
  })

  const { getEntry: getMinimizedEntry } = useMinimizedModalsContext()

  const {
    draft: emailDraft,
    draftId,
    setDraft: setEmailDraft,
  } = useEmailDraft()
  const emailInitial = useEmailCreationFormInitialValues({
    account: activeAccountForCreate,
  })

  const {
    checkAsyncMassMailingTaskStatus,
    getAsyncTaskId,
    handleAddFinishMassMailingNotification,
  } = useMassMailingAsyncAction({
    onCloseModal: onCloseEmailFormModal,
    onReload,
  })

  const handleUpdateTaskEmail = useAsyncCallback(
    result => {
      const { data } = result
      const params = getThreadParams(result)

      return updateTaskEmail(data.activityId, params).then(() => {
        handleAddFinishMassMailingNotification(result)
      })
    },
    [handleAddFinishMassMailingNotification]
  )

  function getHandleSubmitEmail({ activityId, field, requestId }) {
    return async payload => {
      const { data } = await sendRequestTaskEmail({
        data: {
          ...payload,
          audience: TASK_ACTION_SEND_AUDIENCE[field],
          subject: payload.title,
        },
        id: requestId,
        outboundUuid,
      })

      return { data: { ...data, activityId } }
    }
  }

  function handleSuccessSendingEmail(result) {
    const asyncTask = getAsyncTaskId(result.data)

    if (asyncTask) {
      checkAsyncMassMailingTaskStatus(
        {
          ...result,
          task: asyncTask,
        },
        handleUpdateTaskEmail
      )
    } else {
      handleUpdateTaskEmail(result)
    }
  }

  function getHandleRequestDataByTask(requestId, field) {
    return async () => {
      const { listApi } = REQUEST_API_ALIASES[REQUEST_ENTITY.single]

      const { data } = await postApi({
        data: getRequestDataByTaskParams(requestId),
        endpoint: listApi,
      })

      // data.items[0][field] can be array and object
      return toUserSelectItems([].concat(data.items[0][field]))
    }
  }

  function openEmailCreationPopUp({
    action,
    field,
    initialValues,
    item,
    sourceId,
  }) {
    const { flat, id, request } = item

    const flatId = flat?.id
    const requestId = request?.id

    const templateEntityApiParams = getEmailFormTemplateEntityApiParams({
      flatId,
      requestId,
    })

    onSetEmailFormModal(
      <EmailCreationPopUp
        aiLocalContext={{
          object_id: String(id),
          type: AITextGenerationLocalContextType.ActivityEmail,
        }}
        fieldsProps={getEmailCreationPopUpFieldsProps({
          loadRecipientsInitialValue: getHandleRequestDataByTask(
            requestId,
            field
          ),
          recipientsFilterModalInitialValues,
          withServiceAccount,
        })}
        formFieldsProps={{
          PriorityFieldComponent: null,
          attachProps,
          templateListApiParams,
        }}
        getContainerIsDirty={() => isDraftOpenAction(action)}
        getIsDirty={isDirtyTableEmailCreationForm}
        initialValues={initialValues}
        minimizedModalSourceId={sourceId}
        singleTemplateFolder={EMAIL_CREATION_FORM_TEMPLATE_FOLDER_REQUEST}
        templateEntityApiParams={templateEntityApiParams}
        onClose={onCloseEmailFormModal}
        onSubmit={getHandleSubmitEmail({
          activityId: id,
          field,
          requestId: requestId,
        })}
        onSuccess={handleSuccessSendingEmail}
      />
    )
  }

  function handleOpenEmailCreationPopUp({
    action,
    field,
    initialValues,
    item,
  }) {
    const sourceId = getEmailCreationPopUpSourceId({ draftId, item })

    resolveMinimizedDraftEntryOpen({
      action,
      entry: getMinimizedEntry(MINIMIZED_MODAL_KEY.EMAIL_FORM),
      sourceId,
      onOpen: () =>
        openEmailCreationPopUp({
          action,
          field,
          initialValues,
          item,
          sourceId,
        }),
    })
  }

  function openEmailCreateActionSelectModal({ field, item }) {
    onSetModal(
      <EmailDraftCreateActionSelectModal
        draft={emailDraft}
        initialValues={emailInitial}
        onCancel={onCloseModal}
        onSubmit={({ action, initialValues }) => {
          onCloseModal()
          clearEmailDraftIfCreateNew(action, setEmailDraft)
          handleOpenEmailCreationPopUp({ action, field, initialValues, item })
        }}
      />
    )
  }

  return {
    handleOpenEmailPopUp: (item, field) => {
      emailDraft
        ? openEmailCreateActionSelectModal({ field, item })
        : handleOpenEmailCreationPopUp({
            field,
            initialValues: emailInitial,
            item,
          })
    },
  }
}
